import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeftIcon, ClockIcon, MapPinIcon, CalendarDaysIcon, UserIcon } from "@heroicons/react/16/solid";
import { Link } from "../../design/link";
import { Heading, Subheading } from "../../design/heading";
import { Text } from "../../design/text";
import { Divider } from "../../design/divider";
import { Avatar } from "../../design/avatar";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import VisitStatusBadge from "../../components/VisitStatusBadge";
import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import { Visit } from "@visitproof/types";
import { useAuth } from "../../contexts/AuthContext";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import moment from "moment";
import { getInitials } from "../../utils/string";

/**
 * Parent-specific visit detail page
 * Shows only: date, time, location, status, and monitor details
 * Does NOT show: events, reports, case details, or other confidential information
 */
const ParentVisitDetailPage: React.FC = () => {
  const { visitId } = useParams<{ visitId: string }>();
  const { claims, loading: authLoading } = useAuth();
  const [visit, setVisit] = useState<Visit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const accountId = claims?.accountId || null;
  const parentId = accountId ? claims?.accounts?.[accountId]?.parentId || null : null;

  useEffect(() => {
    if (authLoading || !parentId || !visitId) {
      if (!authLoading && !parentId) {
        setError(new Error("Not authorized to view this visit"));
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      doc(db, "visits", visitId),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const visitData = { id: docSnapshot.id, ...docSnapshot.data() } as Visit;

          // Verify parent has access to this visit
          if (!visitData.parentIds?.includes(parentId)) {
            setError(new Error("You don't have access to this visit"));
            setVisit(null);
          } else {
            setVisit(visitData);
            setError(null);
          }
        } else {
          setError(new Error("Visit not found"));
          setVisit(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error loading visit:", err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authLoading, parentId, visitId]);

  if (loading || authLoading) {
    return <LoadingView />;
  }

  if (error) {
    return <ErrorView error={error} />;
  }

  if (!visit) {
    return <ErrorView error={new Error("Visit not found")} />;
  }

  const startTime = moment(visit.startDate.toDate());
  const endTime = moment(visit.endDate.toDate());
  const duration = moment.duration(endTime.diff(startTime));

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <div className="max-lg:hidden">
        <Link
          href="/visits"
          className="inline-flex items-center gap-2 transition-colors text-sm/6 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
        >
          <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
          My Visits
        </Link>
      </div>

      {/* Visit Header */}
      <div className="mt-4 lg:mt-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Heading>{startTime.format("MMMM Do, YYYY")}</Heading>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <VisitStatusBadge status={visit.status} />
              <span className="flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400">
                <ClockIcon className="size-4" />
                {startTime.format("h:mm A")} - {endTime.format("h:mm A")}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400">
                <CalendarDaysIcon className="size-4" />
                {`${duration.hours()}h ${duration.minutes()}m`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Visit Details - Simplified for Parents */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Schedule & Location Card */}
        <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <Subheading>Visit Details</Subheading>
          <Divider className="my-4" />
          <DescriptionList>
            <DescriptionTerm>Date</DescriptionTerm>
            <DescriptionDetails>
              {startTime.format("dddd, MMMM Do, YYYY")}
            </DescriptionDetails>

            <DescriptionTerm>Time</DescriptionTerm>
            <DescriptionDetails>
              {startTime.format("h:mm A")} - {endTime.format("h:mm A")}
            </DescriptionDetails>

            <DescriptionTerm>Duration</DescriptionTerm>
            <DescriptionDetails>
              {duration.hours() > 0 && `${duration.hours()} hour${duration.hours() !== 1 ? 's' : ''} `}
              {duration.minutes() > 0 && `${duration.minutes()} minute${duration.minutes() !== 1 ? 's' : ''}`}
            </DescriptionDetails>

            {visit.location && (
              <>
                <DescriptionTerm>Location</DescriptionTerm>
                <DescriptionDetails>
                  <div className="flex items-start gap-2">
                    <MapPinIcon className="size-4 mt-0.5 text-zinc-400" />
                    <div>
                      {visit.location.name && (
                        <Text className="font-medium">{visit.location.name}</Text>
                      )}
                      {visit.location.formattedAddress && (
                        <Text className="text-zinc-500 dark:text-zinc-400">
                          {visit.location.formattedAddress}
                        </Text>
                      )}
                    </div>
                  </div>
                </DescriptionDetails>
              </>
            )}

            <DescriptionTerm>Status</DescriptionTerm>
            <DescriptionDetails>
              <VisitStatusBadge status={visit.status} />
            </DescriptionDetails>
          </DescriptionList>
        </div>

        {/* Monitor Card */}
        <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <Subheading>Your Monitor</Subheading>
          <Divider className="my-4" />

          {visit.supervisor ? (
            <div className="flex items-center gap-4">
              <Avatar
                src={visit.supervisor.avatarUrl}
                className="size-16"
                initials={getInitials(`${visit.supervisor.firstName} ${visit.supervisor.lastName}`)}
              />
              <div>
                <Text className="text-lg font-medium">
                  {visit.supervisor.firstName} {visit.supervisor.lastName}
                </Text>
                {visit.supervisor.phoneNumber && (
                  <Text className="text-zinc-500 dark:text-zinc-400">
                    {visit.supervisor.phoneNumber}
                  </Text>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800">
              <UserIcon className="size-8 text-zinc-400" />
              <Text className="text-zinc-500 dark:text-zinc-400">
                No monitor assigned yet
              </Text>
            </div>
          )}
        </div>
      </div>

      {/* Cancellation Notice */}
      {visit.status === "canceled" && visit.cancelationReason && (
        <div className="p-4 border border-red-200 rounded-lg bg-red-50 dark:bg-red-900/20 dark:border-red-800">
          <Text className="font-medium text-red-800 dark:text-red-200">
            Cancellation Reason
          </Text>
          <Text className="mt-1 text-red-700 dark:text-red-300">
            {visit.cancelationReason}
          </Text>
        </div>
      )}
    </div>
  );
};

export default ParentVisitDetailPage;
